import { useCallback, useEffect, useRef, useState } from 'react';
import {
  pickAudioMimeType,
  extensionForAudioMime,
} from '@karehero/helpers/files';
import { rmsLevel } from '@karehero/helpers/audio';
import type { RecordingState } from '@karehero/models';
import {
  formatDayMonthTime,
  getTimestampISO,
} from '@karehero/helpers/datetime';
import * as Sentry from '@sentry/react';

import { audioContextClass } from 'utils/audioContext';

/** Said when the microphone is refused, so a caller can tell that apart from a fault. */
export const MIC_DENIED_ERROR =
  'Microphone access was denied. Please allow microphone access in your device settings.';

/** Recordings stop at this length. */
export const MAX_RECORDING_DURATION_SECONDS = 5 * 60;

export interface UseAudioRecorderResult {
  state: RecordingState;
  durationSeconds: number;
  /** Sound levels sampled while recording, 0–1. */
  levels: number[];
  error: string | null;
  start: () => Promise<void>;
  stop: () => void;
  reset: () => void;
  file: File | null;
  /** The microphone, while recording; for anything listening alongside. */
  stream: MediaStream | null;
  isPlaying: boolean;
  togglePlay: () => void;
}

const LEVEL_SAMPLE_MS = 100;
const ANALYSER_FFT_SIZE = 256;

const buildFilename = (mime: string): string => {
  const ext = extensionForAudioMime(mime);
  const label = formatDayMonthTime(getTimestampISO()).replace(/[:/]/g, '-');
  return `recording ${label}.${ext}`;
};

export const useAudioRecorder = (): UseAudioRecorderResult => {
  const [state, setState] = useState<RecordingState>('idle');
  const [durationSeconds, setDurationSeconds] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [levels, setLevels] = useState<number[]>([]);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const audioContextRef = useRef<AudioContext | null>(null);
  const levelTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const levelsRef = useRef<number[]>([]);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const objectUrlRef = useRef<string | null>(null);
  const onEndedRef = useRef<(() => void) | null>(null);

  const stopLevelMeter = useCallback(() => {
    if (levelTimerRef.current) {
      clearInterval(levelTimerRef.current);
      levelTimerRef.current = null;
    }
    // Best effort: the context may already be closed.
    audioContextRef.current?.close().catch(() => undefined);
    audioContextRef.current = null;
  }, []);

  /** Samples loudness from the stream; a no-op without Web Audio. */
  const startLevelMeter = useCallback((stream: MediaStream) => {
    const Context = audioContextClass();
    if (!Context) return;

    const context = new Context();
    const analyser = context.createAnalyser();
    analyser.fftSize = ANALYSER_FFT_SIZE;
    context.createMediaStreamSource(stream).connect(analyser);
    audioContextRef.current = context;

    const samples = new Uint8Array(analyser.fftSize);
    levelsRef.current = [];
    levelTimerRef.current = setInterval(() => {
      analyser.getByteTimeDomainData(samples);
      levelsRef.current = [...levelsRef.current, rmsLevel(samples)];
      setLevels(levelsRef.current);
    }, LEVEL_SAMPLE_MS);
  }, []);

  const cleanup = useCallback(() => {
    stopLevelMeter();
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setStream(null);
    recorderRef.current = null;
  }, [stopLevelMeter]);

  useEffect(() => cleanup, [cleanup]);

  const start = useCallback(async () => {
    setError(null);
    setFile(null);
    setDurationSeconds(0);
    setLevels([]);
    chunksRef.current = [];

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      setStream(stream);
      startLevelMeter(stream);

      const mimeType = pickAudioMimeType();
      const recorder = new MediaRecorder(
        stream,
        mimeType ? { mimeType } : undefined,
      );
      recorderRef.current = recorder;

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        const usedMime = recorder.mimeType || mimeType || 'audio/webm';
        const blob = new Blob(chunksRef.current, { type: usedMime });
        setFile(new File([blob], buildFilename(usedMime), { type: usedMime }));
        setState('stopped');
        cleanup();
      };

      recorder.start();
      setState('recording');

      timerRef.current = setInterval(() => {
        setDurationSeconds((s) => s + 1);
      }, 1000);
    } catch (err) {
      cleanup();
      if (err instanceof DOMException && err.name === 'NotAllowedError') {
        setError(MIC_DENIED_ERROR);
      } else {
        setError('Could not start recording. Please check your microphone.');
      }
    }
  }, [cleanup, startLevelMeter]);

  const stop = useCallback(() => {
    if (recorderRef.current?.state === 'recording') {
      recorderRef.current.stop();
    }
  }, []);

  const cleanupPlayback = useCallback(() => {
    if (audioRef.current) {
      if (onEndedRef.current) {
        audioRef.current.removeEventListener('ended', onEndedRef.current);
        onEndedRef.current = null;
      }
      audioRef.current.pause();
      audioRef.current = null;
    }
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
    setIsPlaying(false);
  }, []);

  useEffect(() => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    objectUrlRef.current = url;
    const audio = new Audio(url);
    const onEnded = () => setIsPlaying(false);
    audio.addEventListener('ended', onEnded);
    onEndedRef.current = onEnded;
    audioRef.current = audio;
    return cleanupPlayback;
  }, [file, cleanupPlayback]);

  const togglePlay = useCallback(() => {
    const el = audioRef.current;
    if (!el) return;
    if (isPlaying) {
      el.pause();
      el.currentTime = 0;
      setIsPlaying(false);
      return;
    }
    el.play()
      .then(() => setIsPlaying(true))
      .catch((err) => {
        // Playback can be refused (autoplay policy, decode failure). Surface it
        // as a recorder error so the composer can show it, rather than leaving
        // the button stuck in a playing state.
        Sentry.captureException(err);
        setIsPlaying(false);
        setError('That recording could not be played back.');
      });
  }, [isPlaying]);

  const reset = useCallback(() => {
    cleanupPlayback();
    cleanup();
    setState('idle');
    setDurationSeconds(0);
    setFile(null);
    setError(null);
    setLevels([]);
    chunksRef.current = [];
  }, [cleanup, cleanupPlayback]);

  return {
    state,
    durationSeconds,
    levels,
    error,
    start,
    stop,
    reset,
    file,
    stream,
    isPlaying,
    togglePlay,
  };
};
